import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { CompoundFilter, Filter, LeafFilter } from '@itonics/filters';

import { take } from 'rxjs/operators';
import { AGGREGATION_TYPES } from '../../../constants/constants';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FilterByCriteria } from '@innovation-os-ng/common';
import { DropdownMenuModule } from '@innovation-os-ng/ui/dropdown-menu';
import { FormatNumPipe } from '@innovation-os-ng/ui/utils';
import { TranslateModule } from '@itonics/ngx-translate-simple';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject } from 'rxjs';
import { NewsfeedFilterIds } from '../../../constants/widget-filters';
import { FeedWidgetService } from '../../../contracts/feed-widget.service';
import { AggregationCount } from '../../../interfaces/evaluation';
import { FilterQuery } from '../../../interfaces/feed';
import { FeedFilterService } from '../services/feed-filter.service';

@Component({
  selector: 'signals-filter-source',
  templateUrl: './filter-source.component.html',
  styleUrls: ['./filter-source.component.scss'],
  standalone: true,
  imports: [CommonModule, NgSelectModule, DropdownMenuModule, TranslateModule, FormatNumPipe, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SourceFromFilterComponent implements OnChanges, OnDestroy {
  @Input()
  public filter: Filter;

  @Input()
  public signalUris: Array<string>;

  @Input()
  public filterIdentifier: string;

  @Input()
  public filterBy: FilterByCriteria;

  @Input()
  public loadFeedByEvaluation: boolean;

  /**
   * Used to apply any style class to the selector component
   */
  @Input()
  public selectorClass: string;

  /**
   * Used to prevent placeholder displaying and replaced with the counter
   */
  @Input()
  public useMinimal = false;

  @Output()
  private readonly onSourceChanged: EventEmitter<Array<string>> = new EventEmitter<Array<string>>();

  public model: Array<AggregationCount>;

  public sources: Array<AggregationCount>;

  private selectedTypes: Array<string> = [];

  /**
   * Flag that indicate whether changes are there or not.
   */
  private isDirty: boolean = false;

  private unsubscribe = new Subject<void>();

  /**
   * Holds the feeds filter.
   */
  private feedFilter: CompoundFilter;

  /**
   * If any filter or this filter is loading this will resolve to true (used with async pipe)
   */
  public readonly isLoading = this.feedFilterService.isLoading;

  constructor(
    private readonly feedWidgetService: FeedWidgetService,
    private readonly feedFilterService: FeedFilterService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe.next();
  }

  /**
   * Listen to input change
   *
   * @param changes - Simple change
   */
  public ngOnChanges(): void {
    // Fetch and store feed filter.
    this.feedFilter = this.feedFilterService.getFeedFilter(this.filter as CompoundFilter, this.filterIdentifier);

    this.updateAggregationCount();

    // If filter is reset we want to remove the applied changes in the dropdown.
    this.removeFilterIfQueryNotFound();
  }

  /**
   * called when the dropdown is closed
   */
  public onClose() {
    if (this.isDirty) {
      this.triggerUpdate();
      this.isDirty = false;
    }
  }

  /**
   * Sets the dirty to true so that an update can be triggered when closed
   */
  public onChange(): void {
    this.isDirty = true;
  }

  /**
   * Perform the actions needed to trigger an update to the filters
   * If multiple selection has no changes, check if single selection model has changes
   * If either resolves to true then emit the selected changes
   */
  public triggerUpdate() {
    // We only submit on close the dropdown
    // not on changes to avoid trigger too many requests

    // we want here to check if there have been any changes after clean the model
    if (this.selectedTypes?.length === 0 && this.model?.length === 0) {
      // we skip
      return;
    }

    // Update selected types as well.
    this.selectedTypes = (this.model || []).map((d) => d?.key);

    this.onSourceChanged.emit(this.selectedTypes);
  }

  /**
   * Fetch aggregation detail from the uri's of signals provided.
   */
  public getAggregationDetailByUris(): void {
    // For source aggregation we need to remove type filter
    const filterAsCompound: Filter = this.removeSourceFilter();

    this.feedWidgetService
      .getSignalsDetail(this.feedFilterService.buildFeedQuery(this.signalUris, filterAsCompound, [AGGREGATION_TYPES.SOURCE]))
      .pipe(take(1))
      .subscribe((response) => {
        this.sources = response.aggregation?.source ?? [];
        this.cdr.detectChanges();
      });
  }

  public compareWith(aItem: AggregationCount, bItem: AggregationCount): boolean {
    // We are modifying the items list all the time
    // when we request new aggregations for that, we need to use
    // compareWith in order to avoid select twice the same item

    return aItem.key === bItem.key;
  }

  /**
   * Fetch aggregation detail with the filter provided.
   */
  public getAggregationDetailByFilter(): void {
    // For source aggregation we need to remove type filter
    const filterAsCompound: Filter = this.removeSourceFilter();
    const feedQuery: FilterQuery = this.feedFilterService.buildFeedQuery(this.signalUris, filterAsCompound, [AGGREGATION_TYPES.SOURCE]);

    this.feedFilterService
      .getFeedEndpoint(feedQuery, this.loadFeedByEvaluation, this.filterBy)
      .pipe(take(1))
      .subscribe((response) => {
        this.sources = response.aggregation?.source ?? [];
        this.applySelectedSourceToModel();
        this.cdr.detectChanges();
      });
  }

  /**
   * Remove the items from the model.
   *
   * Used when clear found button is clicked.
   *
   * @param value - De-selected value
   */
  public removeFromList(value: Array<AggregationCount>): void {
    const clearValues: Array<string> = value.map((it) => it.key);

    // Remove the selected.
    this.model = this.model.filter((m) => !clearValues.includes(m.key));
    this.isDirty = true;
  }

  /**
   * Add the selected item to the list so that
   * the selected item is always shown.
   *
   * If already present then ignores it.
   *
   * Note: Need to do because selected items was getting
   * removed from the list after any other filter was applied
   * and data was refetched.
   */
  private addSelectedItemToList(): void {
    if (!this.model.length) {
      return;
    }

    // Add the already selected items in the list.
    let selectedItems = [...this.model];

    // If we have sources length then
    // else we skip the part and add all to the list.
    if (this.sources?.length) {
      this.model?.forEach((sl) => {
        sl.doc_count = sl.doc_count || 0;
        const index: number = this.sources?.findIndex((s) => s.key === sl.key);

        if (index > -1) {
          // If exits filter out no need to add.
          selectedItems = selectedItems.filter((v) => v.key !== sl.key);
        } else {
          // we clear the count as it is not in the source
          sl.doc_count = 0;
        }
      });
    } else {
      // We reset the count because we don't have any signals if sources are empty
      selectedItems = selectedItems.map((sl) => ({ key: sl.key, doc_count: 0 }));
    }

    // Re assign to reflect changes
    this.sources = [...selectedItems, ...this.sources];
  }

  /**
   * Fetch and update the aggregate count.
   */
  public updateAggregationCount(): void {
    if (this.signalUris?.length) {
      this.getAggregationDetailByUris();
      return;
    }

    if (this.filter) {
      this.getAggregationDetailByFilter();
    }
  }

  /**
   * We want to remove the filter if the new filter in the input
   * does not contains the specific source filter.
   *
   * Note: Used when we want to reset the applied filter.
   */
  private removeFilterIfQueryNotFound(): void {
    if (!this.feedFilter) {
      this.model = [];
      return;
    }

    const sourceFilter = this.getSourceFilter(this.feedFilter);

    if (!sourceFilter) {
      this.model = [];
    }
  }

  /**
   * Provides the source filter from the filter object.
   *
   * @param feedFilter - Feed filter
   * @returns - Filter related with source.
   */
  private getSourceFilter(feedFilter: CompoundFilter): LeafFilter {
    return feedFilter?.getQuery(NewsfeedFilterIds.SOURCE_FILTER) as LeafFilter;
  }

  /**
   * Apply current filter with the model of the dropdown.
   */
  private applySelectedSourceToModel(): void {
    const sourceFilter: LeafFilter = this.getSourceFilter(this.feedFilter);

    if (!this.model?.length && sourceFilter) {
      this.model = this.getTypesAggregationFromString(sourceFilter.getValues() as Array<string>);

      /**
       * If still model is empty or model because sources might be empty
       * or url has different value, then populate model from
       * url because we want to slow the list of selected items.
       */
      if (this.model?.length !== sourceFilter.getValues()?.length) {
        this.updateModelFromUrl(sourceFilter);
      }
    }

    // Update selected types as well.
    this.selectedTypes = this.model?.map((m) => m?.key);

    this.addSelectedItemToList();
  }

  /**
   * Apply the model from the values in the URL
   *
   * @param sourceFilter - Currently applied source filter.
   */
  private updateModelFromUrl(sourceFilter: LeafFilter): void {
    /**
     * Get the list form the url and then populate the list
     *
     * Note: Done so that selected items are always shown in the list.
     */
    (sourceFilter.getValues() as Array<string>)?.forEach((item) => {
      const itemInModel: AggregationCount = this.model.find((i) => i.key === item);

      // If item already in model then ignore it.
      if (itemInModel) {
        return;
      }

      const aggregateCount: AggregationCount = {
        key: item,
        doc_count: 0
      };

      this.model.push(aggregateCount);
    });
  }

  /**
   * Removes the source filter and return the new compound filter.
   *
   * @returns - Returns the compound filter with source removed.
   */
  private removeSourceFilter(): CompoundFilter {
    if (this.filter) {
      const filterAsCompound = this.filter.clone() as CompoundFilter;
      const feedFilterQuery = this.feedFilterService.getFeedFilter(filterAsCompound, this.filterIdentifier);
      if (feedFilterQuery) {
        const sourceFilter = this.getSourceFilter(feedFilterQuery);
        if (sourceFilter) {
          feedFilterQuery.removeFilter(sourceFilter);
        }
      }
      return filterAsCompound;
    }
    return null;
  }

  /**
   * Get source type with aggregation count.
   *
   * Note: Done so that we can get the aggregation count
   * of the respective source type. Filter only includes the
   * source type but to apply selected filter we need data with
   * aggregation count.
   *
   * @param types - Array of Source type key.
   * @returns - Array of Source Typw with aggregation count
   */
  private getTypesAggregationFromString(types: Array<string>): Array<AggregationCount> {
    if (!this.sources?.length) {
      return [];
    }

    return types.map((tp) => this.sources.find((t) => t.key === tp)).filter(Boolean);
  }
}
